
import React from "react";
import { Site } from "@/services/types";
import SiteDepartmentSEO from "./SiteDepartmentSEO";
import DepartmentList from "./DepartmentList";
import SitesSEO from "./SitesSEO";
import SitesHeader from "./SitesHeader";
import SitesMapWrapper from "./SitesMapWrapper";
import SitesListWrapper from "./SitesListWrapper";
import SitesSEOContent from "./SitesSEOContent";
import SitesCallToAction from "./SitesCallToAction";
import { departmentSEOData } from "./departmentSEOData";

interface SitesSectionProps {
  isDeptPage: boolean;
  deptParam: string | null;
  deptName: string;
  filteredSites: Site[];
  isLoading: boolean;
}

const SitesSection: React.FC<SitesSectionProps> = ({
  isDeptPage,
  deptParam,
  deptName,
  filteredSites,
  isLoading
}) => {
  // Get the appropriate SEO content based on the department
  const seoContent = deptParam && departmentSEOData[deptParam] 
    ? departmentSEOData[deptParam]
    : { title: "", services: [], zones: [] };

  return (
    <div className="flex flex-col min-h-screen bg-gradient-to-br from-fabre-lightgray/20 to-white">
      {/* Données structurées SEO */}
      <SitesSEO 
        sites={filteredSites} 
        isDeptPage={isDeptPage}
        deptName={deptName}
        deptCode={deptParam || undefined}
      />
      
      <main className="flex-grow">
        <section className="py-16 bg-gradient-to-br from-fabre-lightgray/30 to-white">
          <div className="container mx-auto px-4">
            {/* En-tête principal optimisé SEO */}
            <SitesHeader 
              isDeptPage={isDeptPage}
              deptName={deptName}
              deptParam={deptParam}
              filteredSites={filteredSites}
            />

            {/* Carte interactive des sites - Section principale */}
            <SitesMapWrapper sites={filteredSites} isLoading={isLoading} />

            {/* Contenu SEO spécifique aux départements */}
            {isDeptPage && departmentSEOData[deptParam!] && (
              <section className="mb-20" aria-labelledby="dept-seo-heading">
                <article className="bg-gradient-to-br from-fabre-beige/20 to-white rounded-3xl shadow-xl border border-fabre-gold/20 overflow-hidden">
                  <SiteDepartmentSEO 
                    deptName={deptName} 
                    deptCode={deptParam!} 
                    seoContent={seoContent} 
                  />
                </article>
              </section>
            )}

            {/* Navigation départementale si page générale */}
            {!isDeptPage && (
              <section className="mb-20" aria-labelledby="dept-nav-heading">
                <div className="text-center mb-10">
                  <h2 id="dept-nav-heading" className="text-3xl md:text-4xl font-heading font-bold mb-6 text-fabre-gray">
                    Nos zones d'intervention
                  </h2>
                  <div className="w-24 h-1 bg-fabre-green mx-auto mb-6"></div>
                  <p className="text-lg text-fabre-darkteal max-w-2xl mx-auto">
                    Groupe Fabre Environnement intervient dans plusieurs départements. 
                    Sélectionnez votre zone pour découvrir nos sites locaux.
                  </p>
                </div>
                <nav className="bg-white rounded-3xl shadow-xl border border-fabre-teal/10 p-8">
                  <DepartmentList />
                </nav>
              </section>
            )}

            {/* Liste des sites - Affichage unifié */}
            <SitesListWrapper 
              isDeptPage={isDeptPage}
              deptName={deptName}
              filteredSites={filteredSites}
              isLoading={isLoading}
            />

            {/* Contenu SEO enrichi */}
            <SitesSEOContent />

            {/* Call to Action optimisé */}
            <SitesCallToAction />
          </div>
        </section>
      </main>
    </div>
  );
};

export default SitesSection;
